import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useAuthStore } from './auth.store';
import { useAuth } from './useAuth';
import { refreshAccessToken } from './authAxios';
import './session-warning.scss';
const IDLE_TIMEOUT_MS = 120 * 60 * 1000; // 120 minutes of inactivity → show warning
const COUNTDOWN_SECONDS = 5 * 60; // 5-minute countdown before auto-logout
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
const CHANNEL_NAME = 'perrigo-session-channel';
export default function SessionWarningDialog() {
    const [visible, setVisible] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const [refreshing, setRefreshing] = useState(false);
    const { isAuthenticated } = useAuthStore();
    const { logout } = useAuth();
    const idleTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const autoLogoutTimerRef = useRef(null);
    const dialogStartTimeRef = useRef(null);
    const channelRef = useRef(null);
    const isAuthenticatedRef = useRef(isAuthenticated);
    useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);
    const clearCountdownTimers = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (autoLogoutTimerRef.current) {
            clearTimeout(autoLogoutTimerRef.current);
            autoLogoutTimerRef.current = null;
        }
    };
    const doLogout = useCallback(() => {
        clearCountdownTimers();
        setVisible(false);
        channelRef.current?.postMessage('logout');
        logout();
    }, [logout]);
    const startCountdown = useCallback((startTime) => {
        clearCountdownTimers();
        dialogStartTimeRef.current = startTime;
        const tick = () => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = Math.max(COUNTDOWN_SECONDS - elapsed, 0);
            setCountdown(remaining);
            if (remaining <= 0) {
                clearCountdownTimers();
                doLogout();
            }
        };
        tick();
        countdownIntervalRef.current = setInterval(tick, 1000);
        autoLogoutTimerRef.current = setTimeout(doLogout, COUNTDOWN_SECONDS * 1000);
    }, [doLogout]);
    const showWarning = useCallback(() => {
        if (!isAuthenticatedRef.current)
            return;
        const startTime = Date.now();
        setVisible(true);
        startCountdown(startTime);
        channelRef.current?.postMessage({ type: 'show-dialog', startTime });
    }, [startCountdown]);
    const resetIdleTimer = useCallback(() => {
        if (idleTimerRef.current)
            clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(showWarning, IDLE_TIMEOUT_MS);
    }, [showWarning]);
    const handleContinue = async () => {
        clearCountdownTimers();
        setVisible(false);
        channelRef.current?.postMessage('reset');
        resetIdleTimer();
        setRefreshing(true);
        try {
            const ok = await refreshAccessToken();
            if (!ok)
                doLogout();
        }
        finally {
            setRefreshing(false);
        }
    };
    // Wire up activity listeners + BroadcastChannel
    useEffect(() => {
        if (!isAuthenticated)
            return;
        channelRef.current = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current.onmessage = (event) => {
            const { data } = event;
            if (data === 'reset') {
                clearCountdownTimers();
                setVisible(false);
                resetIdleTimer();
            }
            else if (data === 'logout') {
                clearCountdownTimers();
                setVisible(false);
                logout();
            }
            else if (data?.type === 'show-dialog') {
                setVisible(true);
                startCountdown(data.startTime);
            }
        };
        const onActivity = () => {
            if (!visible)
                resetIdleTimer();
        };
        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
        resetIdleTimer();
        return () => {
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
            if (idleTimerRef.current)
                clearTimeout(idleTimerRef.current);
            clearCountdownTimers();
            channelRef.current?.close();
            channelRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);
    const formatCountdown = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };
    const dialogHeader = (_jsxs("div", { className: "session-warning__header", children: [_jsx("span", { className: "session-warning__icon", children: _jsx("i", { className: "pi pi-exclamation-triangle" }) }), _jsx("span", { children: "Session Expiration Warning" })] }));
    return (_jsxs(Dialog, { visible: visible, onHide: handleContinue, header: dialogHeader, className: "session-warning-dialog", modal: true, draggable: false, resizable: false, closable: false, children: [_jsxs("p", { className: "session-warning__message", children: ["Your session will expire in", ' ', _jsx("strong", { children: formatCountdown(countdown) }), ' ', "due to inactivity. Click \u2018Continue Session\u2019 to remain logged in."] }), _jsxs("div", { className: "session-warning__actions", children: [_jsx(Button, { label: "Continue Session", className: "session-warning__continue-btn", onClick: handleContinue, loading: refreshing, disabled: refreshing }), _jsx(Button, { label: "Logout", className: "session-warning__logout-btn", outlined: true, severity: "danger", onClick: doLogout, disabled: refreshing })] })] }));
}
